import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { router } from '@inertiajs/react'
import { Send, Bell, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Template {
  id: number
  name: string
  title: string
  body: string
  icon: string
  category: string
  available_variables: string[]
}

interface AudienceOption {
  value: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  description: string
  stats: AudienceInfo
}

interface AudienceInfo {
  total: number
  with_push: number
  without_push: number
  can_send: boolean
}

interface AudiencesStats {
  all_students: AudienceInfo
  students_with_lesson_today: AudienceInfo
  students_inactive: AudienceInfo
  students_package_expiring: AudienceInfo
  students_with_push: AudienceInfo
}

interface Notification {
  id: number
  title: string
  body: string
  status: string
  audience_type: string | null
  sender_name: string
  recipients_count: number
  sent_count: number
  failed_count: number
  success_rate: number
  sent_at: string
  created_at: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  templates: Template[]
  audiences_stats: AudiencesStats
  notifications: Notification[]
  pagination: Pagination
}

export default function PushNotificationsIndex({ templates, audiences_stats, notifications, pagination }: Props) {


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Enviada</Badge>
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Falhou</Badge>
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getAudienceLabel = (audienceType: string | null) => {
    if (!audienceType) return 'Personalizado'
    const audienceLabels: Record<string, string> = {
      'all_students': 'Todos os Alunos',
      'students_with_lesson_today': 'Aula Hoje',
      'students_inactive': 'Inativos (7+ dias)',
      'students_package_expiring': 'Pacote Vencendo',
      'students_with_push': 'Com Push Ativo',
      'specific_students': 'Seleção Individual'
    }
    return audienceLabels[audienceType] || audienceType
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Notificações Push</h1>
            <p className="text-muted-foreground">
              Envie avisos personalizados para seus alunos
            </p>
          </div>
          <Button size="lg" onClick={() => router.visit('/push_notifications/new')}>
            <Send className="mr-2 h-4 w-4" />
            Nova Campanha
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Alunos com Push</CardTitle>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{audiences_stats.students_with_push.with_push}</div>
              <p className="text-xs text-muted-foreground">
                de {audiences_stats.all_students.total} alunos totais
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Enviadas</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.total_count}</div>
              <p className="text-xs text-muted-foreground">
                Notificações enviadas no total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {notifications.length > 0
                  ? Math.round(
                      notifications.reduce((acc, n) => acc + n.success_rate, 0) / notifications.length
                    )
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground">
                Média de entregas bem sucedidas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Histórico */}
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Notificações</CardTitle>
            <CardDescription>
              Acompanhe as notificações enviadas e seu status de entrega
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                <p className="text-muted-foreground">Nenhuma notificação enviada ainda</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => router.visit('/push_notifications/new')}
                >
                  Enviar primeira notificação
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Público</TableHead>
                      <TableHead>Enviado por</TableHead>
                      <TableHead>Destinatários</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Taxa</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{notification.title}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {notification.body}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getAudienceLabel(notification.audience_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>{notification.sender_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{notification.recipients_count} total</p>
                            <p className="text-xs text-muted-foreground">
                              {notification.sent_count} enviadas • {notification.failed_count} falhas
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(notification.status)}</TableCell>
                        <TableCell>
                          <Badge variant={notification.success_rate >= 80 ? 'default' : 'secondary'}>
                            {notification.success_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {notification.sent_at || notification.created_at}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => router.visit(`/push_notifications/${notification.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Paginação */}
                {pagination.total_pages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} de{' '}
                      {pagination.total_count} notificações
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => router.get(`/push_notifications?page=${pagination.current_page - 1}`)}
                        disabled={pagination.current_page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <span className="text-sm">
                        Página {pagination.current_page} de {pagination.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => router.get(`/push_notifications?page=${pagination.current_page + 1}`)}
                        disabled={pagination.current_page === pagination.total_pages}
                      >
                        Próxima
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
